/**
 * Rate Connect authorization composable
 * Manages route access control for Rate Connect based on:
 * - Campaign status (active/paused vs none)
 * - Eligibility (has partners + Net CPA eligible)
 * - User role (admin vs regular user)
 * - Campaign type (CPC vs CPA)
 * 
 * Returns reactive state that updates when accommodation changes or data refetches.
 * Use canAccessRoute(route.meta) to validate if user can access a route.
 */

import { computed, watch, type ComputedRef } from 'vue'
import { useStore } from 'vuex'
import { useCampaign, usePartners } from '@/components/rateConnect/queries'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'
import { RC_BLOCKED_COUNTRIES } from '@/constants/rcBlockedCountries'
import { useSessionValues } from '@/composables/useSessionValues'
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router, type RouteRecordNormalized } from 'vue-router'

/** Route access check result */
export interface RouteAccessResult {
  allowed: boolean
  redirect?: { name: string } | null
}

/** Default route location */
export interface DefaultRoute {
  name: string
}

/** Return type of useRateConnectAuth composable */
export interface UseRateConnectAuthReturn {
  isLoading: ComputedRef<boolean>
  isError: ComputedRef<boolean>
  hasRateConnect: ComputedRef<boolean>
  isEligible: ComputedRef<boolean>
  userRole: ComputedRef<string | null>
  currentCampaignType: ComputedRef<unknown>
  getDefaultRoute: () => DefaultRoute | null
  canAccessRoute: (routeMeta: RouteRecordNormalized['meta']) => RouteAccessResult
}

/**
 * Rate Connect authentication and access control
 * Manages campaign status, eligibility, and route guards
 * 
 * @returns Auth state and access control functions
 * 
 * @example
 * const { hasRateConnect, isEligible, canAccessRoute } = useRateConnectAuth()
 * if (hasRateConnect.value) { ... }
 */
export function useRateConnectAuth(): UseRateConnectAuthReturn {
  const store = useStore()
  const route: RouteLocationNormalizedLoaded = useRoute()
  const router: Router = useRouter()
  
  const userRole = computed(() => store.state.session?.userRole ?? null)
  const { hasAdminOrSupport } = useSessionValues()
  const selectedOrganisationId = computed(() => store.state.session?.selectedOrganisation?.id)

  const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)

  const {
    isLoading: isPartnersLoading,
    isError: isPartnersError,
    activePartnersData,
    isNetCpaEligible,
  } = usePartners(selectedAccommodationId)

  const {
    isLoading: isCampaignsLoading,
    isError: isCampaignsError,
    isFetching: isCampaignFetching,
    campaignsData,
    currentCampaignType,
  } = useCampaign(selectedAccommodationId)

  const isLoading = computed(() => {
    return isPartnersLoading.value || isCampaignsLoading.value || isCampaignFetching.value
  })

  const isError = computed(() => isPartnersError.value || isCampaignsError.value)

  const hasRateConnect = computed(() => {
    if (campaignsData.value === null) {
      return false
    }

    const result =
      campaignsData.value?.status === 'PAUSED' ||
      campaignsData.value?.status === 'PAUSED_UNTIL' ||
      campaignsData.value?.status === 'ACTIVE' ||
      campaignsData.value?.status === 'PAUSED_FOR_BUDGET'

    return result
  })

  const isEligible = computed(() => {
    const isBlockedCountry = RC_BLOCKED_COUNTRIES.includes(currentAccommodation.value?.['countryId'] as number)
    const hasPartners = (activePartnersData.value ?? []).length > 0
    const isNetCpa = isNetCpaEligible.value
    const result = hasPartners && isNetCpa && !isBlockedCountry

    return result
  })

  /**
   * Get default route based on campaign status
   * @returns Default route or null if loading
   */
  const getDefaultRoute = (): DefaultRoute | null => {
    if (isLoading.value) {
      return null
    }

    if (hasRateConnect.value) {
      return { name: 'rate-connect-running-performance' }
    }

    if (!isEligible.value) {
      return { name: 'rate-connect-not-eligible' }
    }

    return { name: 'rate-connect-no-campaign' }
  }

  // Redirect from setup if accommodation changed and no campaign
  watch(selectedAccommodationId, (newId, oldId) => {
    const isOnSetupRoute = route.path.includes('/setup')

    if (isOnSetupRoute && oldId !== undefined && newId !== oldId && !hasRateConnect.value) {
      router.replace({ name: 'rate-connect-no-campaign' })
    }
  })

  // Redirect from setup if session token changed
  watch(
    () => store.state.session.token,
    () => {
      const isOnSetupRoute = route.path.includes('/setup')

      if (isOnSetupRoute) {
        router.replace({ name: 'rate-connect-no-campaign' })
      }
    },
    { immediate: true }
  )

  /**
   * Check if user can access route based on meta requirements
   * @param routeMeta - Route meta object with requirements
   * @returns Access check result with redirect if denied
   */
  const canAccessRoute = (routeMeta: RouteRecordNormalized['meta']): RouteAccessResult => {
    const requires = (routeMeta?.['requires'] as any) || {}

    if (Object.keys(requires).length === 0) {
      return { allowed: true }
    }

    if (requires.hasRateConnect !== undefined) {
      if (requires.hasRateConnect !== hasRateConnect.value) {
        const redirect = getDefaultRoute()
        return { allowed: false, redirect }
      }
    }

    if (requires.isEligible !== undefined) {
      if (requires.isEligible !== isEligible.value) {
        const redirect = getDefaultRoute()
        return { allowed: false, redirect }
      }
    }

    if (requires.userRole) {
      if (
        (requires.userRole === 'admin' && !hasAdminOrSupport.value) ||
        (requires.userRole !== 'admin' && userRole.value !== requires.userRole)
      ) {
        const redirect = getDefaultRoute()
        return { allowed: false, redirect }
      }
    }

    if (requires.campaignType) {
      if (currentCampaignType.value !== requires.campaignType) {
        return {
          allowed: false,
          redirect: { name: 'rate-connect-running-performance' },
        }
      }
    }

    return { allowed: true }
  }

  return {
    isLoading,
    isError,
    hasRateConnect,
    isEligible,
    userRole,
    currentCampaignType,
    getDefaultRoute,
    canAccessRoute,
  }
}